import { useState, useEffect } from 'react'
import { Card, Table, Button, Modal, Form, Row, Col, Badge, Alert, Spinner, InputGroup, Accordion } from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from '../services/api'

/** Convert domain to safe context name (replace . and - with _) */
const safeDomain = (domain: string) => domain.replace(/[.\-]/g, '_')

interface AmiUser {
  id: string
  username: string
  secret?: string
  description: string | null
  tenant_id: string
  tenant_name: string | null
  context: string | null
  permit: string
  deny: string
  is_active: boolean
  read_permissions: string[]
  write_permissions: string[]
  created_at: string | null
  last_used: string | null
}

interface PermissionPreset {
  id: string
  name: string
  description: string
  read: string[]
  write: string[]
}

interface Tenant {
  id: string
  name: string
  domain: string
}

const ALL_READ_PERMISSIONS = [
  'system', 'call', 'log', 'verbose', 'agent', 'user', 'config',
  'dtmf', 'reporting', 'cdr', 'dialplan', 'originate', 'message'
]

const ALL_WRITE_PERMISSIONS = [
  'system', 'call', 'log', 'verbose', 'agent', 'user', 'config',
  'dtmf', 'reporting', 'cdr', 'dialplan', 'originate', 'message', 'command', 'agi'
]

const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  system: 'System-Ereignisse',
  call: 'Anruf-Ereignisse und -Steuerung',
  log: 'Log-Nachrichten',
  verbose: 'Ausführliche Ausgaben',
  agent: 'Agenten-Steuerung (Queues)',
  user: 'Benutzer-Ereignisse',
  config: 'Konfigurationsänderungen',
  dtmf: 'DTMF-Ereignisse',
  reporting: 'Statistiken und Berichte',
  cdr: 'Anrufdetails (CDR)',
  dialplan: 'Dialplan-Ereignisse',
  originate: 'Anrufe starten',
  message: 'Nachrichten senden',
  command: 'CLI-Befehle ausführen',
  agi: 'AGI-Skripte steuern',
}

export default function AmiUsers() {
  const [amiUsers, setAmiUsers] = useState<AmiUser[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [presets, setPresets] = useState<PermissionPreset[]>([])
  const [extensions, setExtensions] = useState<{ extension: string; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showSecretModal, setShowSecretModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editing, setEditing] = useState<AmiUser | null>(null)
  const [selectedUser, setSelectedUser] = useState<AmiUser | null>(null)
  const [saving, setSaving] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    description: '',
    tenant_id: '',
    permit: '127.0.0.1/255.255.255.255',
    deny: '0.0.0.0/0.0.0.0',
    read_system: false,
    read_call: false,
    read_log: false,
    read_verbose: false,
    read_agent: false,
    read_user: false,
    read_config: false,
    read_dtmf: false,
    read_reporting: false,
    read_cdr: false,
    read_dialplan: false,
    read_originate: false,
    read_message: false,
    write_system: false,
    write_call: false,
    write_log: false,
    write_verbose: false,
    write_agent: false,
    write_user: false,
    write_config: false,
    write_dtmf: false,
    write_reporting: false,
    write_cdr: false,
    write_dialplan: false,
    write_originate: false,
    write_message: false,
    write_command: false,
    write_agi: false,
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [usersRes, presetsRes] = await Promise.all([
        api.get('/ami-users'),
        api.get('/ami-users/permission-presets'),
      ])
      setAmiUsers(usersRes.data.items || [])
      setPresets(presetsRes.data.presets || [])

      // Try to load tenants (only works for super_admin)
      try {
        const tenantsRes = await api.get('/tenants')
        setTenants(tenantsRes.data.items || [])
      } catch {
        // Tenant admins can't list tenants - that's OK
        setTenants([])
      }

      // Load extensions for TAPI documentation
      try {
        const extRes = await api.get('/extensions/')
        setExtensions((extRes.data.items || []).map((e: any) => ({ extension: e.extension, name: e.name })))
      } catch {
        setExtensions([])
      }
    } catch (error) {
      toast.error('Fehler beim Laden der AMI-Benutzer')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      username: '',
      description: '',
      tenant_id: tenants.length > 0 ? tenants[0].id : '',
      permit: '127.0.0.1/255.255.255.255',
      deny: '0.0.0.0/0.0.0.0',
      read_system: false,
      read_call: false,
      read_log: false,
      read_verbose: false,
      read_agent: false,
      read_user: false,
      read_config: false,
      read_dtmf: false,
      read_reporting: false,
      read_cdr: false,
      read_dialplan: false,
      read_originate: false,
      read_message: false,
      write_system: false,
      write_call: false,
      write_log: false,
      write_verbose: false,
      write_agent: false,
      write_user: false,
      write_config: false,
      write_dtmf: false,
      write_reporting: false,
      write_cdr: false,
      write_dialplan: false,
      write_originate: false,
      write_message: false,
      write_command: false,
      write_agi: false,
    })
  }

  const openCreateModal = () => {
    setEditing(null)
    resetForm()
    setShowModal(true)
  }

  const openEditModal = async (user: AmiUser) => {
    try {
      const res = await api.get(`/ami-users/${user.id}`)
      const data = res.data
      setEditing(data)
      setFormData({
        username: data.username,
        description: data.description || '',
        tenant_id: data.tenant_id,
        permit: data.permit || '127.0.0.1/255.255.255.255',
        deny: data.deny || '0.0.0.0/0.0.0.0',
        read_system: data.read_system,
        read_call: data.read_call,
        read_log: data.read_log,
        read_verbose: data.read_verbose,
        read_agent: data.read_agent,
        read_user: data.read_user,
        read_config: data.read_config,
        read_dtmf: data.read_dtmf,
        read_reporting: data.read_reporting,
        read_cdr: data.read_cdr,
        read_dialplan: data.read_dialplan,
        read_originate: data.read_originate,
        read_message: data.read_message,
        write_system: data.write_system,
        write_call: data.write_call,
        write_log: data.write_log,
        write_verbose: data.write_verbose,
        write_agent: data.write_agent,
        write_user: data.write_user,
        write_config: data.write_config,
        write_dtmf: data.write_dtmf,
        write_reporting: data.write_reporting,
        write_cdr: data.write_cdr,
        write_dialplan: data.write_dialplan,
        write_originate: data.write_originate,
        write_message: data.write_message,
        write_command: data.write_command,
        write_agi: data.write_agi,
      })
      setShowModal(true)
    } catch {
      toast.error('Fehler beim Laden')
    }
  }

  const applyPreset = (preset: PermissionPreset) => {
    const newFormData = { ...formData }

    // Reset all permissions first
    ALL_READ_PERMISSIONS.forEach(p => {
      (newFormData as any)[`read_${p}`] = false
    })
    ALL_WRITE_PERMISSIONS.forEach(p => {
      (newFormData as any)[`write_${p}`] = false
    })

    // Apply preset permissions
    preset.read.forEach(p => {
      (newFormData as any)[`read_${p}`] = true
    })
    preset.write.forEach(p => {
      (newFormData as any)[`write_${p}`] = true
    })

    setFormData(newFormData)
    toast.info(`Vorlage "${preset.name}" angewendet`)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      if (editing) {
        // Update - don't send username or tenant_id
        const { username, tenant_id, ...updateData } = formData
        await api.put(`/ami-users/${editing.id}`, updateData)
        toast.success('AMI-Benutzer aktualisiert')
      } else {
        // Create - include tenant_id only for super admins
        const url = formData.tenant_id
          ? `/ami-users?tenant_id=${formData.tenant_id}`
          : '/ami-users'
        await api.post(url, formData)
        toast.success('AMI-Benutzer erstellt')
      }
      setShowModal(false)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedUser) return
    try {
      await api.delete(`/ami-users/${selectedUser.id}`)
      toast.success('AMI-Benutzer gelöscht')
      setShowDeleteModal(false)
      setSelectedUser(null)
      loadData()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const regenerateSecret = async (userId: string) => {
    try {
      await api.post(`/ami-users/${userId}/regenerate-secret`)
      toast.success('Neues Passwort generiert')
      // Update the user in the list with new secret
      const userRes = await api.get(`/ami-users/${userId}`)
      setSelectedUser(userRes.data)
      setShowSecretModal(true)
    } catch {
      toast.error('Fehler beim Generieren')
    }
  }

  const showSecret = async (user: AmiUser) => {
    try {
      const res = await api.get(`/ami-users/${user.id}`)
      setSelectedUser(res.data)
      setShowSecretModal(true)
    } catch {
      toast.error('Fehler beim Laden')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.info('In Zwischenablage kopiert')
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-terminal me-2"></i>
          AMI-Benutzer
        </h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-2"></i>
          Neuer AMI-Benutzer
        </Button>
      </div>

      <Alert variant="info" className="mb-4">
        <i className="bi bi-info-circle me-2"></i>
        <strong>AMI (Asterisk Manager Interface)</strong> ermöglicht externen Anwendungen die Steuerung von Asterisk.
        Jeder AMI-Benutzer ist auf den Context seines Mandanten beschränkt und kann nur dessen Nebenstellen sehen/steuern.
        <br />
        <small className="text-muted">Port: 5038/TCP | Passwörter werden automatisch generiert (mind. 24 Zeichen)</small>
      </Alert>

      <Card>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Benutzername</th>
                <th>Mandant</th>
                <th>Context</th>
                <th>Berechtigungen</th>
                <th>Status</th>
                <th className="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {amiUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    Keine AMI-Benutzer vorhanden
                  </td>
                </tr>
              ) : (
                amiUsers.map(user => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.username}</strong>
                      {user.description && (
                        <div className="text-muted small">{user.description}</div>
                      )}
                    </td>
                    <td>{user.tenant_name || '-'}</td>
                    <td>
                      <code className="small">{user.context}</code>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {user.read_permissions.length > 0 && (
                          <Badge bg="info" className="me-1">
                            R: {user.read_permissions.length}
                          </Badge>
                        )}
                        {user.write_permissions.length > 0 && (
                          <Badge bg="warning" text="dark">
                            W: {user.write_permissions.length}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-1"
                        onClick={() => showSecret(user)}
                        title="Passwort anzeigen"
                      >
                        <i className="bi bi-key"></i>
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openEditModal(user)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user)
                          setShowDeleteModal(true)
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* TAPI Configuration Documentation */}
      {amiUsers.length > 0 && (
        <Accordion className="mt-4">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <i className="bi bi-book me-2"></i>
              <strong>TAPI / PhoneSuite Konfiguration</strong>
            </Accordion.Header>
            <Accordion.Body>
              {(() => {
                // Get tenant domain from first AMI user or tenants list
                const userTenant = tenants.find(t => amiUsers.some(u => u.tenant_id === t.id))
                const tenantDomain = userTenant?.domain || amiUsers[0]?.context?.replace('from-internal-', '') || ''
                const sd = safeDomain(tenantDomain)

                return (
                  <>
                    <h6 className="mb-3">1. AMI-Verbindung</h6>
                    <Table bordered size="sm" className="mb-4" style={{ maxWidth: 500 }}>
                      <tbody>
                        <tr><td style={{ width: 150 }}><strong>Host</strong></td><td><code>{window.location.hostname}</code></td></tr>
                        <tr><td><strong>Port</strong></td><td><code>5038</code></td></tr>
                        <tr><td><strong>Username</strong></td><td><code>{amiUsers.find(u => u.read_permissions.includes('originate'))?.username || amiUsers[0]?.username}</code></td></tr>
                        <tr><td><strong>Secret</strong></td><td><em>(siehe AMI Zugangsdaten oben)</em></td></tr>
                      </tbody>
                    </Table>

                    <h6 className="mb-3">2. Context pro Nebenstelle</h6>
                    <p className="text-muted small mb-2">
                      Jede Nebenstelle hat einen eigenen TAPI-Context. In PhoneSuite muss der Context der jeweiligen Nebenstelle zugewiesen werden:
                    </p>
                    <Table bordered size="sm" className="mb-4">
                      <thead>
                        <tr>
                          <th>Nebenstelle</th>
                          <th>Name</th>
                          <th>TAPI Context</th>
                          <th>Originate Channel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extensions.map(ext => (
                          <tr key={ext.extension}>
                            <td><strong>{ext.extension}</strong></td>
                            <td>{ext.name}</td>
                            <td><code>tapi_{sd}_{ext.extension}</code></td>
                            <td><code>Local/{ext.extension}@tapi_originate_{sd}/n</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <h6 className="mb-3">3. AMI Originate Befehl</h6>
                    <p className="text-muted small mb-2">
                      Um einen Anruf von Nebenstelle <strong>{extensions[0]?.extension || '10'}</strong> an eine Zielnummer zu starten:
                    </p>
                    <pre className="bg-dark text-light p-3 rounded" style={{ fontSize: '0.85em' }}>{`Action: Originate
Channel: Local/${extensions[0]?.extension || '10'}@tapi_originate_${sd}/n
Context: tapi_${sd}_${extensions[0]?.extension || '10'}
Exten: <Zielnummer>
Priority: 1
CallerID: ${extensions[0]?.extension || '10'}
Async: true`}</pre>
                    <Alert variant="info" className="mt-3 mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Originate Channel</strong> klingelt alle registrierten Telefone der Nebenstelle (Tischtelefon, Softphone, etc.).
                      Sobald eines abgenommen wird, wird die Zielnummer gewählt.
                    </Alert>
                  </>
                )
              })()}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? 'AMI-Benutzer bearbeiten' : 'Neuer AMI-Benutzer'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Benutzername *</Form.Label>
                  {!editing ? (
                    <InputGroup>
                      <InputGroup.Text className="bg-light text-muted">
                        {tenants.find(t => t.id === formData.tenant_id)?.domain || 'mandant'}_
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        value={formData.username}
                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                        required
                        placeholder="z.B. api_user"
                      />
                    </InputGroup>
                  ) : (
                    <Form.Control
                      type="text"
                      value={editing.username}
                      disabled
                    />
                  )}
                  <Form.Text className="text-muted">
                    {editing ? 'Benutzername kann nicht geändert werden' : 'Wird automatisch mit Mandanten-Präfix versehen'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {!editing && tenants.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Mandant *</Form.Label>
                    <Form.Select
                      value={formData.tenant_id}
                      onChange={e => setFormData({ ...formData, tenant_id: e.target.value })}
                      required
                    >
                      <option value="">-- Mandant wählen --</option>
                      {tenants.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
                {!editing && tenants.length === 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Mandant</Form.Label>
                    <Form.Control
                      type="text"
                      value="Ihr Mandant (automatisch)"
                      disabled
                    />
                    <Form.Text className="text-muted">
                      Der AMI-Benutzer wird Ihrem Mandanten zugeordnet
                    </Form.Text>
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                type="text"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. CRM-System Integration"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Erlaubte IPs (permit)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.permit}
                    onChange={e => setFormData({ ...formData, permit: e.target.value })}
                    placeholder="127.0.0.1/255.255.255.255"
                  />
                  <Form.Text className="text-muted">
                    IP/Netzmaske der erlaubten Clients
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gesperrte IPs (deny)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.deny}
                    onChange={e => setFormData({ ...formData, deny: e.target.value })}
                    placeholder="0.0.0.0/0.0.0.0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <h6 className="mb-3">
              <i className="bi bi-shield-lock me-2"></i>
              Berechtigungen
            </h6>

            <div className="mb-3">
              <Form.Label className="d-block">Vorlagen:</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {presets.map(preset => (
                  <Button
                    key={preset.id}
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>

            <Row>
              <Col md={6}>
                <h6 className="text-info mb-2">
                  <i className="bi bi-eye me-1"></i> Lesen
                </h6>
                {ALL_READ_PERMISSIONS.map(perm => (
                  <Form.Check
                    key={`read_${perm}`}
                    type="checkbox"
                    id={`read_${perm}`}
                    label={
                      <span>
                        {perm} <small className="text-muted">({PERMISSION_DESCRIPTIONS[perm]})</small>
                      </span>
                    }
                    checked={(formData as any)[`read_${perm}`]}
                    onChange={e => setFormData({ ...formData, [`read_${perm}`]: e.target.checked })}
                    className="mb-1"
                  />
                ))}
              </Col>
              <Col md={6}>
                <h6 className="text-warning mb-2">
                  <i className="bi bi-pencil me-1"></i> Schreiben
                </h6>
                {ALL_WRITE_PERMISSIONS.map(perm => (
                  <Form.Check
                    key={`write_${perm}`}
                    type="checkbox"
                    id={`write_${perm}`}
                    label={
                      <span>
                        {perm} <small className="text-muted">({PERMISSION_DESCRIPTIONS[perm]})</small>
                      </span>
                    }
                    checked={(formData as any)[`write_${perm}`]}
                    onChange={e => setFormData({ ...formData, [`write_${perm}`]: e.target.checked })}
                    className="mb-1"
                  />
                ))}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Secret Modal */}
      <Modal show={showSecretModal} onHide={() => setShowSecretModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-key me-2"></i>
            AMI Zugangsdaten
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Bewahren Sie diese Zugangsdaten sicher auf! Das Passwort wird automatisch generiert und kann nicht manuell geändert werden.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Benutzername</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={selectedUser.username}
                    readOnly
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => copyToClipboard(selectedUser.username)}
                  >
                    <i className="bi bi-clipboard"></i>
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Passwort (Secret)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={selectedUser.secret || '***'}
                    readOnly
                    style={{ fontFamily: 'monospace' }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => selectedUser.secret && copyToClipboard(selectedUser.secret)}
                  >
                    <i className="bi bi-clipboard"></i>
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Context</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedUser.context || '-'}
                  readOnly
                  style={{ fontFamily: 'monospace' }}
                />
                <Form.Text className="text-muted">
                  Der AMI-Benutzer sieht nur Ereignisse aus diesem Context
                </Form.Text>
              </Form.Group>

              <hr />

              <div className="text-center">
                <Button
                  variant="warning"
                  onClick={() => regenerateSecret(selectedUser.id)}
                >
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Neues Passwort generieren
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSecretModal(false)}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>AMI-Benutzer löschen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Möchten Sie den AMI-Benutzer <strong>{selectedUser?.username}</strong> wirklich löschen?
            <br />
            <small>Alle Verbindungen mit diesem Benutzer werden unterbrochen.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="bi bi-trash me-2"></i>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
